// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import NavigationModeMenu, { NavigationModeMenuStyles } from "@insite/content-library/Components/NavigationModeMenu";
import Hidden from "@insite/mobius/Hidden";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const Navigation = styled.div`
    color: ${getContrastColor("common.accent")};
    font-size: 15px;
    background-color: ${getColor("common.accent")};
    padding: 0 45px 0 45px;
    height: 50px;
    text-transform: uppercase;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const NavItem = styled.span<InjectableCss>`
    display: inline-flex;

    > span,
    > a {
        margin-left: 10px;
    }
    ${injectCss}
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 30px;
            min-width: 0;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
};

const SecondaryNavigation: FC<Props> = ({ id, enableWarehousePickup }) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    return (
        <Hidden below="lg">
            <Navigation {...styles.wrapper}>
                <NavItem>
                    <NavigationModeMenu extendedStyles={styles.navigationModeMenu} />
                </NavItem>
                <NavItem>
                    <Zone zoneName="Currency" contentId={id} fixed />
                </NavItem>
                {/* XNG-Changes */}
                {/* {fields.showInventoryToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                    <NavItem
                        className="show-inventory-toggle"
                        data-test-selector="showInventoryToggle"
                        {...styles.showInventoryLink}
                    >
                        <ShowInventoryToggle extendedStyles={styles.showInventoryToggle} />
                    </NavItem>
                )}
                {fields.showPricingToggle && displayPricingAndInventory && currentMode === "Storefront" && (
                    <NavItem
                        className="show-pricing-toggle"
                        data-test-selector="showPricingToggle"
                        {...styles.showPricingLink}
                    >
                        <ShowPricingToggle extendedStyles={styles.showPricingToggle} />
                    </NavItem>
                )} */}
                <NavItem {...styles.languageLink}>
                    <Zone zoneName="Language" contentId={id} fixed />
                </NavItem>
                {enableWarehousePickup && (
                    <NavItem {...styles.warehousePickupLink}>
                        <Zone zoneName="ShipToAddress" contentId={id} fixed />
                    </NavItem>
                )}
                <NavItem {...styles.signInLink}>
                    <Zone zoneName="SignIn" contentId={id} fixed />
                </NavItem>
            </Navigation>
        </Hidden>
    );
};

const secondaryNavigation: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigation),
    definition: {
        group: "Common",
        icon: "link-simple",
    },
};

export default secondaryNavigation;
